/**
 * E2E Flow Custom Nodes
 * Issue #312: E2E flow renderer for End-to-End User Interaction Flow Diagrams
 *
 * Custom nodes for visualizing E2E user flows from frontend action to database.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { FlowStepType } from '@veodiagram/shared';
import { ConfidenceBadge } from '../../../shared/ConfidenceBadge';

// ============= Node Data Interface =============

interface E2EFlowNodeData {
  label: string;
  type: FlowStepType;
  description?: string;
  sourceFile?: string;
  sourceLine?: number;
  isAsync?: boolean;
  duration?: number;
  error?: {
    type: string;
    message: string;
  };
  method?: string;
  endpoint?: string;
  operation?: string;
  model?: string;
  table?: string;
  triggerType?: string;
  // Issues #345-353: Enhanced fields
  navigationTargetDetails?: {
    path: string;
    component: string;
    isDynamic: boolean;
  };
  domEffects?: {
    conditionalRender?: string[];
    cssClassChanges?: string[];
    affectedElements?: string[];
  };
  clipboardContent?: {
    contentType: string;
    preview?: string;
    confirmationUI?: string;
  };
  formValidation?: {
    fields: string[];
    validationRules?: string[];
    errorDisplay?: string;
  };
  apiSummary?: {
    method: string;
    endpoint: string;
    requestBody?: string[];
    errorHandling?: string;
  };
  affectedElements?: Array<{
    elementId: string;
    changeType: string;
    description: string;
  }>;
  // Issue #390: Enhanced step data
  databaseStepData?: {
    operation: string;
    table: string;
    model?: string;
    library?: string;
    fields?: string[];
    whereFields?: string[];
    joins?: Array<{ table: string; type: string }>;
    inTransaction?: boolean;
  };
  apiRouteStepData?: {
    method: string | string[];
    path: string;
    handlerName?: string;
    framework?: string;
    middleware?: string[];
    requestFields?: string[];
    responseFields?: string[];
    pathParams?: string[];
  };
  apiCallStepData?: {
    method: string;
    endpoint: string;
    isDynamic?: boolean;
    library?: string;
    pathParams?: string[];
    wrapperFunction?: string;
  };
  backendLogicStepData?: {
    functionName: string;
    logicType: string;
    operations: string[];
    inputFields?: string[];
    outputFields?: string[];
  };
  // Issue #430: Batch/call counts for aggregated operations
  callCount?: number;
  batchCount?: number;
  // Confidence indicator for heuristic vs deterministic detections
  confidence?: 'deterministic' | 'heuristic';
  confidenceReason?: string;
  [key: string]: unknown;
}

// ============= Step Type Colors =============

const STEP_TYPE_COLORS: Record<FlowStepType, { bg: string; border: string; text: string; handle: string; icon: string }> = {
  'user-action': { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af', handle: '#3b82f6', icon: '👆' },
  'ui-component': { bg: '#f3e8ff', border: '#a855f7', text: '#7e22ce', handle: '#a855f7', icon: '🧩' },
  'api-call': { bg: '#fef3c7', border: '#f59e0b', text: '#b45309', handle: '#f59e0b', icon: '📤' },
  'api-route': { bg: '#d1fae5', border: '#10b981', text: '#047857', handle: '#10b981', icon: '🔀' },
  'backend-logic': { bg: '#e0e7ff', border: '#8b5cf6', text: '#5b21b6', handle: '#8b5cf6', icon: '⚡' },  // Issue #390: Backend processing
  'service-call': { bg: '#e0e7ff', border: '#6366f1', text: '#4338ca', handle: '#6366f1', icon: '⚙️' },
  'database': { bg: '#fce7f3', border: '#ec4899', text: '#be185d', handle: '#ec4899', icon: '💾' },
  'external-api': { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c', handle: '#ef4444', icon: '🌐' },
  'state-change': { bg: '#cffafe', border: '#06b6d4', text: '#0e7490', handle: '#06b6d4', icon: '📊' },
  'navigation': { bg: '#f0fdf4', border: '#22c55e', text: '#15803d', handle: '#22c55e', icon: '🧭' },
  'navigation-target': { bg: '#dbeafe', border: '#2563eb', text: '#1e3a8a', handle: '#2563eb', icon: '📱' },  // Issue #345
  'modal-container': { bg: '#f5f5f4', border: '#78716c', text: '#44403c', handle: '#78716c', icon: '🚪' },  // Issue #348
  'modal-outcome': { bg: '#f8fafc', border: '#94a3b8', text: '#475569', handle: '#94a3b8', icon: '↳' },  // Issue #348
  'prop-callback': { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', handle: '#f59e0b', icon: '↗️' },  // Issue #357
  'api-response': { bg: '#d1fae5', border: '#059669', text: '#065f46', handle: '#059669', icon: '📥' },  // Issue #369: Response from backend
  'api-callback': { bg: '#fef3c7', border: '#d97706', text: '#92400e', handle: '#d97706', icon: '⚡' },  // Issue #369: Frontend callback
  'dom-update': { bg: '#dcfce7', border: '#16a34a', text: '#166534', handle: '#16a34a', icon: '✨' },    // Issue #369: Final DOM change
  'error': { bg: '#fef2f2', border: '#dc2626', text: '#991b1b', handle: '#dc2626', icon: '⚠️' },
  'cache': { bg: '#fdf4ff', border: '#d946ef', text: '#a21caf', handle: '#d946ef', icon: '💨' },
  // Issue #898: middleware steps emitted before api-route + pre-action
  // confirm gates (#898 F3, F5).
  'middleware': { bg: '#ecfeff', border: '#0891b2', text: '#155e75', handle: '#0891b2', icon: '🛡️' },
  'pre-action-gate': { bg: '#fff7ed', border: '#ea580c', text: '#9a3412', handle: '#ea580c', icon: '🚦' },
};

// ============= Helper Functions =============

const getShortPath = (filePath?: string): string => {
  if (!filePath) return '';
  const parts = filePath.split('/');
  return parts.slice(-2).join('/');
};

// veodiagram-issues#45.A — detect external HTTP URLs in step endpoints.
// Mirrors Signal 1 of the cross-repo classifier
// (scripts/cross-repo-classify.mjs): an absolute URL with an http(s) or
// protocol-relative prefix is external unless the analyzer has already
// classified it (in which case `data.type` is `external-api` and we
// short-circuit). We deliberately do NOT replicate Signal 3
// (route-table match) on the frontend — the analyzer is the source of
// truth for "this absolute URL is actually our own backend"; if it
// missed, the user resolves via the #41 review modal.
const ABSOLUTE_URL_RE = /^(?:https?:)?\/\//i;
const UNRESOLVED_URL_RE = /\{dynamic\}|\$\{/;

export function isExternalEndpoint(endpoint?: string | null): boolean {
  if (!endpoint || UNRESOLVED_URL_RE.test(endpoint)) return false;
  return ABSOLUTE_URL_RE.test(endpoint);
}

export function extractHost(endpoint?: string | null): string | null {
  if (!endpoint) return null;
  try {
    const withScheme = endpoint.startsWith('//') ? `http:${endpoint}` : endpoint;
    return new URL(withScheme).host || null;
  } catch {
    const m = endpoint.match(/^(?:https?:)?\/\/([^/?#]+)/i);
    return m?.[1] ?? null;
  }
}

// ============= Base E2E Flow Node =============

interface BaseE2EFlowNodeProps {
  data: E2EFlowNodeData;
  selected: boolean;
  isStart?: boolean;
  isEnd?: boolean;
}

function BaseE2EFlowNode({ data, selected, isStart, isEnd }: BaseE2EFlowNodeProps) {
  const colors = STEP_TYPE_COLORS[data.type] || STEP_TYPE_COLORS['service-call'];

  return (
    <div
      className={`
        relative flex flex-col px-3 py-2 min-w-[160px] max-w-[240px]
        rounded-lg border-2 transition-all duration-200
        ${selected ? 'ring-2 ring-blue-500 ring-offset-2 shadow-lg' : 'shadow-sm'}
        ${isStart ? 'border-l-4 border-l-green-500' : ''}
        ${isEnd ? 'border-r-4 border-r-red-500' : ''}
      `}
      style={{
        backgroundColor: colors.bg,
        borderColor: colors.border,
      }}
    >
      {/* Top Target Handle - for vertical same-column edges */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="!w-3 !h-3 !border-2 !border-white"
        style={{
          backgroundColor: colors.handle,
          opacity: isStart ? 0 : 1, // Hide visually for start nodes
        }}
      />

      {/* Bottom Source Handle - for vertical same-column edges */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="!w-3 !h-3 !border-2 !border-white"
        style={{
          backgroundColor: colors.handle,
          opacity: isEnd ? 0 : 1, // Hide visually for end nodes
        }}
      />

      {/* Issue #428: Left Handle - for return edges (server→client, database→server) */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="!w-2.5 !h-2.5 !border-2 !border-white"
        style={{
          backgroundColor: colors.handle,
          top: '50%',
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-source"
        className="!w-2.5 !h-2.5 !border-2 !border-white"
        style={{
          backgroundColor: colors.handle,
          top: '50%',
        }}
      />

      {/* Issue #428: Right Handle - for outgoing edges (client→server, server→database) */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!w-2.5 !h-2.5 !border-2 !border-white"
        style={{
          backgroundColor: colors.handle,
          top: '50%',
        }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-target"
        className="!w-2.5 !h-2.5 !border-2 !border-white"
        style={{
          backgroundColor: colors.handle,
          top: '50%',
        }}
      />

      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="text-base">{colors.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <div
              className="font-semibold text-xs truncate"
              style={{ color: colors.text }}
              title={data.label}
            >
              {data.label}
            </div>
            <ConfidenceBadge confidence={data.confidence} reason={data.confidenceReason} />
          </div>
          {data.description && (
            <div className="text-[10px] text-gray-500 truncate" title={data.description}>
              {data.description}
            </div>
          )}
        </div>
      </div>

      {/* Details based on type */}
      <div className="mt-1.5 text-[10px] text-gray-600">
        {data.type === 'api-call' && data.method && data.endpoint && (
          <div className="font-mono" title={`${data.method} ${data.endpoint}`}>
            {/* #45.A — surface the host on a separate line when the URL is
                external so it stays readable without expanding the card. */}
            {isExternalEndpoint(data.endpoint) && extractHost(data.endpoint) && (
              <div className="text-[9px] text-gray-500 truncate">
                {extractHost(data.endpoint)}
              </div>
            )}
            <div className="truncate">
              {data.method} {data.endpoint}
            </div>
          </div>
        )}
        {data.type === 'api-route' && data.endpoint && (
          <div className="font-mono truncate" title={data.endpoint}>
            {data.method || 'GET'} {data.endpoint}
          </div>
        )}
        {/* Issue #390: Enhanced database display with fields */}
        {data.type === 'database' && (data.operation || data.model || data.table) && (
          <div>
            <div className="font-mono truncate">
              {data.operation} {data.model || data.table}
            </div>
            {data.databaseStepData?.fields && data.databaseStepData.fields.length > 0 && (
              <div className="text-[9px] text-gray-500 truncate" title={`Fields: ${data.databaseStepData.fields.join(', ')}`}>
                📋 {data.databaseStepData.fields.slice(0, 3).join(', ')}{data.databaseStepData.fields.length > 3 ? '...' : ''}
              </div>
            )}
            {data.databaseStepData?.whereFields && data.databaseStepData.whereFields.length > 0 && (
              <div className="text-[9px] text-gray-500 truncate" title={`Where: ${data.databaseStepData.whereFields.join(', ')}`}>
                🔍 {data.databaseStepData.whereFields.join(', ')}
              </div>
            )}
          </div>
        )}
        {/* Issue #390: Enhanced API route display with request/response fields */}
        {data.type === 'api-route' && data.apiRouteStepData && (
          <div>
            {data.apiRouteStepData.requestFields && data.apiRouteStepData.requestFields.length > 0 && (
              <div className="text-[9px] text-gray-500 truncate" title={`Request: ${data.apiRouteStepData.requestFields.join(', ')}`}>
                📥 req: {data.apiRouteStepData.requestFields.slice(0, 3).join(', ')}{data.apiRouteStepData.requestFields.length > 3 ? '...' : ''}
              </div>
            )}
            {data.apiRouteStepData.responseFields && data.apiRouteStepData.responseFields.length > 0 && (
              <div className="text-[9px] text-gray-500 truncate" title={`Response: ${data.apiRouteStepData.responseFields.join(', ')}`}>
                📤 res: {data.apiRouteStepData.responseFields.slice(0, 3).join(', ')}{data.apiRouteStepData.responseFields.length > 3 ? '...' : ''}
              </div>
            )}
          </div>
        )}
        {data.type === 'external-api' && data.endpoint && (
          <div className="font-mono" title={data.endpoint}>
            {/* #45.A — surface the host above the path so a Stripe / ipapi
                call is identifiable at a glance, not buried in the URL. */}
            {extractHost(data.endpoint) && (
              <div className="text-[9px] text-gray-500 truncate">
                {extractHost(data.endpoint)}
              </div>
            )}
            <div className="truncate">→ {data.endpoint}</div>
          </div>
        )}
        {/* Issue #390: Backend logic display */}
        {data.type === 'backend-logic' && data.backendLogicStepData && (
          <div>
            <div className="font-mono truncate">
              {data.backendLogicStepData.functionName}
            </div>
            {data.backendLogicStepData.operations.length > 0 && (
              <div className="text-[9px] text-gray-500 truncate" title={data.backendLogicStepData.operations.join(', ')}>
                {data.backendLogicStepData.operations.slice(0, 2).join(', ')}{data.backendLogicStepData.operations.length > 2 ? '...' : ''}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1 mt-1.5">
        {/* #45.A — "External" pill on any step whose endpoint leaves the
            system: confirmed external-api steps + api-call steps with an
            absolute URL the analyzer didn't reclassify. Tooltip shows
            the host for context. Placed first so it's the most prominent
            signal in the badges row. */}
        {(data.type === 'external-api' ||
          (data.type === 'api-call' && isExternalEndpoint(data.endpoint))) && (
          <span
            className="text-[9px] px-1 py-0.5 rounded bg-gray-200 text-gray-700 border border-gray-300"
            title={`This call leaves the system${extractHost(data.endpoint) ? ` (host: ${extractHost(data.endpoint)})` : ''}`}
          >
            ↗ External
          </span>
        )}
        {data.isAsync && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 text-blue-700">
            async
          </span>
        )}
        {data.duration && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-700">
            {data.duration}ms
          </span>
        )}
        {data.type === 'error' && data.error && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-700">
            {data.error.type}
          </span>
        )}
        {/* Issue #346: DOM effects badge */}
        {data.domEffects && (data.domEffects.conditionalRender?.length || data.domEffects.cssClassChanges?.length) && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-100 text-cyan-700" title="DOM effects">
            👁️ effects
          </span>
        )}
        {/* Issue #350: Clipboard badge */}
        {data.clipboardContent && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-green-100 text-green-700" title={data.clipboardContent.confirmationUI}>
            📋 {data.clipboardContent.contentType}
          </span>
        )}
        {/* Issue #351: Form validation badge */}
        {data.formValidation && data.formValidation.fields.length > 0 && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700" title={`Fields: ${data.formValidation.fields.join(', ')}`}>
            📝 {data.formValidation.fields.length} fields
          </span>
        )}
        {/* Issue #352: API summary badge */}
        {data.apiSummary && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-700" title={`${data.apiSummary.method} ${data.apiSummary.endpoint}`}>
            🔗 {data.apiSummary.method}
          </span>
        )}
        {/* Issue #345: Navigation target badge */}
        {data.navigationTargetDetails && data.navigationTargetDetails.isDynamic && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 text-purple-700" title="Dynamic route">
            🔄 dynamic
          </span>
        )}
        {/* Issue #390: Database library badge */}
        {data.databaseStepData?.library && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-pink-100 text-pink-700" title={`Library: ${data.databaseStepData.library}`}>
            {data.databaseStepData.library}
          </span>
        )}
        {/* Issue #390: Database joins badge */}
        {data.databaseStepData?.joins && data.databaseStepData.joins.length > 0 && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700" title={`Joins: ${data.databaseStepData.joins.map(j => j.table).join(', ')}`}>
            🔗 {data.databaseStepData.joins.length} join{data.databaseStepData.joins.length > 1 ? 's' : ''}
          </span>
        )}
        {/* Issue #390: Transaction badge */}
        {data.databaseStepData?.inTransaction && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-orange-100 text-orange-700" title="In transaction">
            🔄 txn
          </span>
        )}
        {/* Issue #390: API route middleware badge */}
        {data.apiRouteStepData?.middleware && data.apiRouteStepData.middleware.length > 0 && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-700" title={`Middleware: ${data.apiRouteStepData.middleware.join(', ')}`}>
            🔧 {data.apiRouteStepData.middleware.length} mw
          </span>
        )}
        {/* Issue #430: Call count badge for deduplicated API calls */}
        {data.callCount && data.callCount > 1 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold" title={`Called ${data.callCount} times`}>
            ×{data.callCount}
          </span>
        )}
        {/* Issue #430: Batch count badge for batched database operations */}
        {data.batchCount && data.batchCount > 1 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800 font-semibold" title={`${data.batchCount} identical operations batched`}>
            ×{data.batchCount}
          </span>
        )}
      </div>

      {/* Source location */}
      {data.sourceFile && (
        <div className="mt-1 text-[9px] text-gray-400 truncate font-mono">
          {getShortPath(data.sourceFile)}
          {data.sourceLine && `:${data.sourceLine}`}
        </div>
      )}
    </div>
  );
}

// ============= Specialized E2E Flow Nodes =============

export const UserActionNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'user-action' } as E2EFlowNodeData}
    selected={selected}
    isStart
  />
));
UserActionNode.displayName = 'UserActionNode';

export const UIComponentNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'ui-component' } as E2EFlowNodeData}
    selected={selected}
  />
));
UIComponentNode.displayName = 'UIComponentNode';

export const ApiCallNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'api-call' } as E2EFlowNodeData}
    selected={selected}
  />
));
ApiCallNode.displayName = 'ApiCallNode';

export const ApiRouteNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'api-route' } as E2EFlowNodeData}
    selected={selected}
  />
));
ApiRouteNode.displayName = 'ApiRouteNode';

export const ServiceCallNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'service-call' } as E2EFlowNodeData}
    selected={selected}
  />
));
ServiceCallNode.displayName = 'ServiceCallNode';

export const DatabaseNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'database' } as E2EFlowNodeData}
    selected={selected}
    isEnd
  />
));
DatabaseNode.displayName = 'DatabaseNode';

export const ExternalApiNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'external-api' } as E2EFlowNodeData}
    selected={selected}
  />
));
ExternalApiNode.displayName = 'ExternalApiNode';

export const StateChangeNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'state-change' } as E2EFlowNodeData}
    selected={selected}
  />
));
StateChangeNode.displayName = 'StateChangeNode';

export const NavigationNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'navigation' } as E2EFlowNodeData}
    selected={selected}
  />
));
NavigationNode.displayName = 'NavigationNode';

// Issue #345: Navigation target node (screen/page)
export const NavigationTargetNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'navigation-target' } as E2EFlowNodeData}
    selected={selected}
  />
));
NavigationTargetNode.displayName = 'NavigationTargetNode';

// Issue #390: Backend logic node
export const BackendLogicNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'backend-logic' } as E2EFlowNodeData}
    selected={selected}
  />
));
BackendLogicNode.displayName = 'BackendLogicNode';

export const ErrorNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'error' } as E2EFlowNodeData}
    selected={selected}
    isEnd
  />
));
ErrorNode.displayName = 'ErrorNode';

export const CacheNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'cache' } as E2EFlowNodeData}
    selected={selected}
  />
));
CacheNode.displayName = 'CacheNode';

// Issue #348: Modal container node for grouped modal interactions
export const ModalContainerNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'modal-container' } as E2EFlowNodeData}
    selected={selected}
  />
));
ModalContainerNode.displayName = 'ModalContainerNode';

// Issue #348: Modal outcome node showing interaction result
export const ModalOutcomeNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'modal-outcome' } as E2EFlowNodeData}
    selected={selected}
  />
));
ModalOutcomeNode.displayName = 'ModalOutcomeNode';

// Issue #357: Prop callback node for cross-component communication
export const PropCallbackNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'prop-callback' } as E2EFlowNodeData}
    selected={selected}
  />
));
PropCallbackNode.displayName = 'PropCallbackNode';

// Issue #369: API response node (response from backend)
export const ApiResponseNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'api-response' } as E2EFlowNodeData}
    selected={selected}
  />
));
ApiResponseNode.displayName = 'ApiResponseNode';

// Issue #369: API callback node (frontend handler for response)
export const ApiCallbackNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'api-callback' } as E2EFlowNodeData}
    selected={selected}
  />
));
ApiCallbackNode.displayName = 'ApiCallbackNode';

// Issue #369: DOM update node (final visual change)
export const DomUpdateNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'dom-update' } as E2EFlowNodeData}
    selected={selected}
    isEnd
  />
));
DomUpdateNode.displayName = 'DomUpdateNode';

// Issue #898 F3 + #1071: middleware step (e.g. authenticate, validate,
// rate-limit) runs server-side before the api-route handler. Renders
// with the BaseE2EFlowNode template + the 🛡️ icon / cyan tone from
// STEP_TYPE_COLORS / STEP_TYPE_LABELS.
export const MiddlewareNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'middleware' } as E2EFlowNodeData}
    selected={selected}
  />
));
MiddlewareNode.displayName = 'MiddlewareNode';

// Issue #898 F5 + #1071: client-side pre-action confirmation gate
// (window.confirm / modal). Sits between user-action and api-call when
// the user has to acknowledge before the flow proceeds.
export const PreActionGateNode = memo(({ data, selected }: NodeProps) => (
  <BaseE2EFlowNode
    data={{ ...data, type: 'pre-action-gate' } as E2EFlowNodeData}
    selected={selected}
  />
));
PreActionGateNode.displayName = 'PreActionGateNode';

export const E2EFlowGenericNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as E2EFlowNodeData;
  return <BaseE2EFlowNode data={nodeData} selected={selected} />;
});
E2EFlowGenericNode.displayName = 'E2EFlowGenericNode';

// ============= Issue #427: Swimlane Layout Nodes =============

/**
 * Swimlane column header node
 * Displays the column title (CLIENT, SERVER, DATABASE)
 */
interface SwimlaneHeaderData {
  label: string;
  column: 'client' | 'server' | 'database';
  color: string;
  bgColor: string;
  width: number;
}

export const SwimlaneHeaderNode = memo(({ data }: NodeProps) => {
  const headerData = data as unknown as SwimlaneHeaderData;

  return (
    <div
      className="flex items-center justify-center px-4 py-2 rounded-t-lg font-bold text-sm tracking-wider"
      style={{
        backgroundColor: headerData.color,
        color: 'white',
        width: headerData.width,
        minWidth: 120,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      <span className="flex items-center gap-2">
        {headerData.column === 'client' && '🖥️'}
        {headerData.column === 'server' && '⚙️'}
        {headerData.column === 'database' && '💾'}
        {headerData.label}
      </span>
    </div>
  );
});
SwimlaneHeaderNode.displayName = 'SwimlaneHeaderNode';

// ============= Node Type Registry =============

export const e2eFlowNodeTypes = {
  'user-action': UserActionNode,
  'ui-component': UIComponentNode,
  'api-call': ApiCallNode,
  'api-route': ApiRouteNode,
  'backend-logic': BackendLogicNode,      // Issue #390
  'service-call': ServiceCallNode,
  'database': DatabaseNode,
  'external-api': ExternalApiNode,
  'state-change': StateChangeNode,
  'navigation': NavigationNode,
  'navigation-target': NavigationTargetNode,  // Issue #345
  'modal-container': ModalContainerNode,  // Issue #348
  'modal-outcome': ModalOutcomeNode,      // Issue #348
  'prop-callback': PropCallbackNode,      // Issue #357
  'api-response': ApiResponseNode,        // Issue #369
  'api-callback': ApiCallbackNode,        // Issue #369
  'dom-update': DomUpdateNode,            // Issue #369
  'error': ErrorNode,
  'cache': CacheNode,
  'e2eFlowNode': E2EFlowGenericNode,
  // Issue #898 F3 / #1071: register middleware so it renders with the
  // BaseE2EFlowNode template instead of React Flow's default fallback.
  'middleware': MiddlewareNode,
  // Issue #898 F5 / #1071: same — client-side confirm-gate step.
  'pre-action-gate': PreActionGateNode,
  // Issue #427: Swimlane layout nodes
  'swimlaneHeader': SwimlaneHeaderNode,
};

/**
 * Get the appropriate node type string for a flow step type.
 */
export function getE2EFlowNodeType(type: FlowStepType): string {
  if (type in e2eFlowNodeTypes) {
    return type;
  }
  return 'e2eFlowNode';
}

/**
 * Get colors for a step type
 */
export function getStepTypeColors(type: FlowStepType): { bg: string; border: string; text: string } {
  return STEP_TYPE_COLORS[type] || STEP_TYPE_COLORS['service-call'];
}

/**
 * Get all step type labels for legend
 */
export const STEP_TYPE_LABELS: Record<FlowStepType, string> = {
  'user-action': 'User Action',
  'ui-component': 'UI Component',
  'api-call': 'API Call',
  'api-route': 'API Route',
  'backend-logic': 'Backend Logic',  // Issue #390
  'service-call': 'Service Call',
  'database': 'Database',
  'external-api': 'External API',
  'state-change': 'State Change',
  'navigation': 'Navigation',
  'navigation-target': 'Screen',  // Issue #345
  'modal-container': 'Modal',     // Issue #348
  'modal-outcome': 'Outcome',     // Issue #348
  'prop-callback': 'Callback',    // Issue #357
  'api-response': 'Response',     // Issue #369
  'api-callback': 'Callback',     // Issue #369
  'dom-update': 'DOM Update',     // Issue #369
  'error': 'Error',
  'cache': 'Cache',
  'middleware': 'Middleware',         // Issue #898 F3
  'pre-action-gate': 'Confirm Gate',  // Issue #898 F5
};
